import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { saveData } from 'services/api/saveData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { SUBCONTRACTOR_SETTINGS_ROUTE } from 'services/subcontractors/useSubcontractorDayPassCheck';

// The page the bank redirects to after paying a day pass order
const DayPassThankYouPage: React.FC = () => {
    const { t } = useTranslation();
    const { environmentHash } = useAuthContext();
    const history = useHistory();
    const [isProcessing, setIsProcessing] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [balance, setBalance] = useState<number | null>(null);

    // Finalize the day pass order after the ideal redirect
    useEffect(() => {
        const finalize = async () => {

            // Get the payment intent and status from the url
            const url = new URL(window.location.href);
            const paymentIntent = url.searchParams.get('payment_intent');
            const status = url.searchParams.get('redirect_status');

            // Stop when the payment did not succeed
            if (status !== 'succeeded' || !paymentIntent) {
                setIsProcessing(false);
                setIsSuccess(false);
                return;
            }

            try {
                // Finalize the order, the backend books idempotently
                const response = await saveData({
                    apiUrl: `finalize_day_pass_order/${environmentHash}`,
                    method: 'post',
                    data: { payment_intent: paymentIntent }
                });

                // Show the new balance on success
                if (response?.status === 200) {
                    setBalance(response.data.balance);
                    setIsSuccess(true);
                }
            } catch (error) {
                console.error('Finalize failed', error);
            } finally {
                setIsProcessing(false);
            }
        };

        finalize();
    }, []);

    // Show a loader while the payment is processed
    if (isProcessing) {
        return (
            <div className='container-filled thank-you-page'>
                <div className='loader'></div>
            </div>
        );
    }

    return (
        <div className='container-filled thank-you-page'>
            {isSuccess ? (
                <>
                    <h2>{t('subcontractor.day_pass.thank_you_title')}</h2>
                    <p>{t('subcontractor.day_pass.thank_you_description', { balance })}</p>
                </>
            ) : (
                <>
                    <h2>{t('subcontractor.day_pass.payment_failed_title')}</h2>
                    <p>{t('subcontractor.day_pass.payment_failed_description')}</p>
                </>
            )}
            <PrimaryButton
                type='button'
                label='subcontractor.day_pass.back_to_settings_button'
                size='small'
                onClick={() => history.push(SUBCONTRACTOR_SETTINGS_ROUTE)}
            />
        </div>
    );
};

export default DayPassThankYouPage;
